<script setup lang="ts">
import { computed, ref } from 'vue'
import TabIcon from './TabIcon.vue'
import SubchargeCircle from './SubchargeCircle.vue'
import { useNavigation } from './useNavigation'
import { useStudentScreen } from './useStudentScreen'
import type { Student2Context } from '@/types/student2'

const props = defineProps<{ context: Student2Context; standalone?: boolean }>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { isMobileLocal } = useStudentScreen()
const { getLink: $getLink, gotoOnClick: $gotoOnClick } = useNavigation()

const autoModeInfoModal = ref(false)

const smartbook = computed(() => props.context.smartbook ?? {})

const canChargeFromAnyUnit = computed(() => {
  return smartbook.value.development_state !== 'full' && !props.context.student.teachers?.length
})

const text = computed(() => {
  switch (smartbook.value.lock_reason) {
    case 'need_purchase':
      return 'В демо-режиме доступен только первый юнит Умного Учебника. Чтобы продолжить, купите Умный Учебник.'

    case 'out_of_charge':
      return '"Батарейка" вашего Умного Учебника села. Чтобы продолжить, прослушайте аудиоурок.'

    case 'tasks_incomplete':
      return "Для доступа к Умному Учебнику выполните задания предыдущих юнитов в Pupil's Book"

    case 'locked_by_teacher':
      return 'Доступ к юниту регулируется вашим учителем.'

    default:
      return ''
  }
})

const lastUnlockedUnit = computed(() => {
  const units = props.context.course_part.units
  const unitId = props.context.smartbook?.last_unit_unlocked_by_progress
  return units.find(e => e.id === unitId) ?? units[0]
})

const openAutoModeInfo = () => {
  autoModeInfoModal.value = true
}

const closeAutoModeInfo = () => {
  autoModeInfoModal.value = false
}

const goHome = (event: Event) => {
  $gotoOnClick(event)
  emit('update:modelValue', false)
}
</script>

<template>
  <div class="student-smartbook-lock-reason" :class="{ 'student-smartbook-lock-reason_standalone': standalone }">
    <template v-if="smartbook.lock_reason === 'out_of_charge'">
      <SubchargeCircle :size="isMobileLocal ? 64 : 96" :stroke="8" alt-icon />
    </template>
    <template v-if="smartbook.lock_reason === 'tasks_incomplete'">
      <TabIcon tab="roundup" :size="isMobileLocal ? 56 : 96" />
    </template>
    <template v-if="smartbook.lock_reason === 'locked_by_teacher'">
      <div class="student-smartbook-lock-reason__teacher-icon">
        <v-icon :size="isMobileLocal ? 36 : 64">mdi-school</v-icon>
      </div>
    </template>

    <p
      class="student-smartbook-lock-reason__body"
      :style="smartbook.lock_reason === 'need_purchase' ? 'grid-column: 1 / 3' : ''"
    >
      {{ text }}
    </p>

    <div class="student-smartbook-lock-reason__actions">
      <template v-if="smartbook.lock_reason === 'need_purchase'">
        <v-button
          :mode="isMobileLocal ? 'outlined small' : 'outlined'"
          style="background: white"
          :href="$getLink({ unitId: context.course_part.units[0].id })"
          @click="$gotoOnClick"
        >
          <v-icon>mdi-chevron-left</v-icon>
          Открыть {{ context.course_part.units[0].title }}
        </v-button>

        <v-button
          :mode="isMobileLocal ? 'filled primary small' : 'filled primary'"
          :href="$getLink({ hash: 'shop' })"
          @click="$gotoOnClick"
        >
          Купить
        </v-button>
      </template>
      <template v-if="smartbook.lock_reason === 'out_of_charge'">
        <v-button
          :mode="isMobileLocal ? 'filled warn small' : 'filled warn'"
          :href="$getLink({ unitId: canChargeFromAnyUnit ? undefined : lastUnlockedUnit.id, tab: 'audio' })"
          @click="$gotoOnClick"
        >
          Зарядить батарейку
        </v-button>
        <v-button :mode="isMobileLocal ? 'outlined primary small' : 'outlined primary'" @click="openAutoModeInfo">
          <v-icon class="mr-half">mdi-information-outline</v-icon>
          Информация
        </v-button>
      </template>
      <v-button
        v-if="smartbook.lock_reason === 'tasks_incomplete'"
        :mode="isMobileLocal ? 'filled primary small' : 'filled primary'"
        :href="$getLink({ unitId: lastUnlockedUnit.id, tab: 'roundup', hidden: 'true' })"
        @click="$gotoOnClick"
      >
        Pupil's Book
      </v-button>
      <v-button
        v-if="smartbook.lock_reason === 'locked_by_teacher'"
        :mode="isMobileLocal ? 'outlined small' : 'outlined'"
        style="background: white"
        :href="$getLink({ unitId: lastUnlockedUnit.id })"
        @click="$gotoOnClick"
      >
        <v-icon>mdi-chevron-left</v-icon>
        Открыть {{ lastUnlockedUnit.title }}
      </v-button>
    </div>

    <v-button
      v-if="standalone"
      :mode="isMobileLocal ? 'outlined primary small' : 'outlined primary'"
      :href="$getLink({ tab: 'home' })"
      class="student-smartbook-lock-reason__home-btn"
      @click="goHome"
    >
      <v-icon size="24" class="mr-1">logo_small</v-icon>
      На главную
    </v-button>

    <v-rich-modal v-model="autoModeInfoModal" title="Информация для родителей" force-desktop>
      <div style="max-width: min(550px, 80vmax)">
        <p class="mb-1">
          Если Вы хотите делегировать платформе контроль успешности обучения по программе, оставайтесь
          в режиме <b>Auto</b>!
        </p>
        <p>
          Он поможет обеспечить необходимое количество прослушиваний аудиоуроков, благодаря "разряжающейся" батарейке,
          и переведёт ребёнка на следующий юнит только после правильного выполнения заданий в Контрольном Уроке.
        </p>
      </div>

      <template #actions>
        <v-button
          :mode="isMobileLocal ? 'filled primary block small' : 'filled primary block'"
          @click="closeAutoModeInfo"
        >
          Понятно
        </v-button>
      </template>
    </v-rich-modal>
  </div>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-smartbook-lock-reason {
  display: grid;
  grid-template-columns: 96px 1fr;
  grid-auto-flow: column;
  align-items: center;
  gap: 16px 24px;

  @include s2.mobile {
    grid-template-columns: 64px 1fr;
    gap: 8px 16px;
    font-size: 14px;
  }

  &__teacher-icon {
    background: $lightenBlue;
    color: $blue;
    padding: 16px;
    border: 1px solid $lightBlue;
    border-radius: 8px;

    @include s2.mobile {
      width: 56px;
      padding: 8px;
    }
  }

  &:not(&_standalone) {
    max-width: min(500px, 90%);
    background: white;
    padding: 16px;
    border-radius: 16px;
    box-shadow:
      0 4px 8px rgba(0, 0, 0, 0.1),
      0 0 2px rgba(0, 0, 0, 0.2);
  }

  &__body {
    grid-column: 2;
  }

  &__actions {
    grid-row: 2;
    grid-column: 1 / 3;

    display: flex;
    gap: 8px;

    .v-button {
      flex: 1;
    }
  }

  &__home-btn {
    grid-row: 3;
    grid-column: 1 / 3;
  }
}
</style>
